import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { fetchAllBugReports, updateBugReportStatus } from '../services/firebase';
import { theme } from '../styles/theme';
import BugReportIcon from '@mui/icons-material/BugReport';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';

const BugReports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frameFilter, setFrameFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const loadReports = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setRefreshing(true);
      }
      const data = await fetchAllBugReports();
      setReports(data);
    } catch (error) {
      console.error('Erro ao carregar bug reports:', error);
      alert('Erro ao carregar bug reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleRefresh = useCallback(() => {
    loadReports();
  }, [loadReports]);

  const handleStatusChange = async (userId: string, reportId: string, newStatus: string) => {
    setUpdatingStatus(reportId);
    try {
      await updateBugReportStatus(userId, reportId, newStatus);
      
      // Atualizar estado local
      setReports(prevReports =>
        prevReports.map(report =>
          report.id === reportId ? { ...report, status: newStatus } : report
        )
      );
      
      // Feedback visual
      alert('Status atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reported':
        return '#FF9800'; // Laranja
      case 'viewed':
        return '#2196F3'; // Azul
      case 'development':
        return '#9C27B0'; // Roxo
      case 'done':
        return '#4CAF50'; // Verde
      default:
        return '#9E9E9E'; // Cinza
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'reported':
        return 'Reportado';
      case 'viewed':
        return 'Visualizado';
      case 'development':
        return 'Em Desenvolvimento';
      case 'done':
        return 'Concluído';
      default:
        return status;
    }
  };

  const calculateMetrics = useCallback(() => {
    const total = reports.length;
    const reported = reports.filter(r => r.status === 'reported').length;
    const viewed = reports.filter(r => r.status === 'viewed').length;
    const development = reports.filter(r => r.status === 'development').length;
    const done = reports.filter(r => r.status === 'done').length;
    
    return { total, reported, viewed, development, done };
  }, [reports]);

  const getAllFrames = useCallback(() => {
    const framesSet = new Set<string>();
    reports.forEach(report => {
      if (report.frames && Array.isArray(report.frames)) {
        report.frames.forEach((frame: string) => framesSet.add(frame));
      }
    });
    return Array.from(framesSet).sort();
  }, [reports]);

  const filterAndSortReports = useCallback(() => {
    let filtered = [...reports];

    // Filtrar por termo de busca (nome do usuário)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(report =>
        report.userName?.toLowerCase().includes(term) ||
        report.userEmail?.toLowerCase().includes(term) ||
        report.title?.toLowerCase().includes(term)
      );
    }

    // Filtrar por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    // Filtrar por frame
    if (frameFilter !== 'all') {
      filtered = filtered.filter(report =>
        report.frames && report.frames.includes(frameFilter)
      );
    }

    // Ordenar por data
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [reports, searchTerm, statusFilter, frameFilter, sortOrder]);

  const groupByUser = useCallback((reports: any[]) => {
    const grouped: { [userId: string]: any[] } = {};
    
    reports.forEach(report => {
      if (!grouped[report.userId]) {
        grouped[report.userId] = [];
      }
      grouped[report.userId].push(report);
    });
    
    return grouped;
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const difference = now.getTime() - date.getTime();
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor(difference / (1000 * 60));

      if (days === 0) {
        if (hours === 0) {
          return `${minutes}m atrás`;
        }
        return `${hours}h atrás`;
      } else if (days === 1) {
        return 'Ontem';
      } else if (days < 7) {
        return `${days}d atrás`;
      } else {
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
      }
    } catch (e) {
      return 'Data inválida';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // 🚀 Memoizar cálculos pesados para evitar re-computação desnecessária
  const filteredReports = useMemo(() => filterAndSortReports(), [filterAndSortReports]);
  const groupedReports = useMemo(() => groupByUser(filteredReports), [groupByUser, filteredReports]);
  const metrics = useMemo(() => calculateMetrics(), [calculateMetrics]);
  const allFrames = useMemo(() => getAllFrames(), [getAllFrames]);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Carregando bug reports...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <BugReportIcon sx={{ fontSize: 32, color: theme.colors.primary }} />
          <h1 style={styles.title}>Bug Reports</h1>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            ...styles.refreshButton,
            opacity: refreshing ? 0.6 : 1,
            cursor: refreshing ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshIcon sx={{ fontSize: 20, marginRight: '8px' }} />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {/* Métricas */}
      <div style={styles.metricsContainer}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{metrics.total}</div>
          <div style={styles.metricLabel}>Total</div>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: `4px solid #FF9800` }}>
          <div style={styles.metricValue}>{metrics.reported}</div>
          <div style={styles.metricLabel}>Reportados</div>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: `4px solid #2196F3` }}>
          <div style={styles.metricValue}>{metrics.viewed}</div>
          <div style={styles.metricLabel}>Visualizados</div>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: `4px solid #9C27B0` }}>
          <div style={styles.metricValue}>{metrics.development}</div>
          <div style={styles.metricLabel}>Em Desenvolvimento</div>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: `4px solid #4CAF50` }}>
          <div style={styles.metricValue}>{metrics.done}</div>
          <div style={styles.metricLabel}>Concluídos</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={styles.filtersContainer}>
        <div style={styles.searchBox}>
          <SearchIcon sx={{ color: '#999', marginRight: '8px' }} />
          <input
            type="text"
            placeholder="Buscar por usuário ou título..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Todos os status</option>
          <option value="reported">Reportado</option>
          <option value="viewed">Visualizado</option>
          <option value="development">Em Desenvolvimento</option>
          <option value="done">Concluído</option>
        </select>

        <select
          value={frameFilter}
          onChange={(e) => setFrameFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Todas as telas</option>
          {allFrames.map(frame => (
            <option key={frame} value={frame}>{frame}</option>
          ))}
        </select>

        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          style={styles.sortButton}
        >
          {sortOrder === 'desc' ? '↓ Mais recente' : '↑ Mais antigo'}
        </button>
      </div>

      {/* Lista de Reports */}
      {Object.keys(groupedReports).length === 0 ? (
        <div style={styles.emptyState}>
          <BugReportIcon sx={{ fontSize: 80, color: '#ccc' }} />
          <h3 style={{ color: '#666', marginTop: '16px' }}>Nenhum bug report encontrado</h3>
          <p style={{ color: '#999' }}>
            {searchTerm || statusFilter !== 'all' || frameFilter !== 'all'
              ? 'Tente ajustar os filtros'
              : 'Quando usuários reportarem bugs, eles aparecerão aqui'}
          </p>
        </div>
      ) : (
        <div style={styles.reportsContainer}>
          {Object.entries(groupedReports).map(([userId, userReports]) => {
            const firstReport = userReports[0];
            return (
              <div key={userId} style={styles.userGroup}>
                {/* Header do Usuário */}
                <div style={styles.userHeader}>
                  {firstReport.userPhotoUrl ? (
                    <img
                      src={firstReport.userPhotoUrl}
                      alt={firstReport.userName}
                      style={styles.userAvatar}
                    />
                  ) : (
                    <div style={styles.userAvatarPlaceholder}>
                      <PersonIcon sx={{ color: '#999' }} />
                    </div>
                  )}
                  <div style={styles.userInfo}>
                    <div style={styles.userName}>{firstReport.userName}</div>
                    <div style={styles.userEmail}>{firstReport.userEmail}</div>
                  </div>
                  <div style={styles.userReportCount}>
                    {userReports.length} {userReports.length === 1 ? 'report' : 'reports'}
                  </div>
                </div>

                {/* Reports do Usuário */}
                <div style={styles.reportsGrid}>
                  {userReports.map((report) => {
                    const isExpanded = expandedReportId === report.id;
                    const statusColor = getStatusColor(report.status);
                    
                    return (
                      <div key={report.id} style={styles.reportCard}>
                        {/* Barra de Status */}
                        <div
                          style={{
                            ...styles.statusBar,
                            backgroundColor: statusColor + '33',
                          }}
                        >
                          <div
                            style={{
                              ...styles.statusDot,
                              backgroundColor: statusColor,
                            }}
                          />
                          <span style={{ ...styles.statusText, color: statusColor }}>
                            {getStatusLabel(report.status)}
                          </span>
                        </div>

                        {/* Conteúdo do Card */}
                        <div
                          style={styles.cardContent}
                          onClick={() => setExpandedReportId(isExpanded ? null : report.id)}
                        >
                          <div style={styles.cardTop}>
                            {/* Thumbnail da imagem */}
                            {report.photoUrl && (
                              <img
                                src={report.photoUrl}
                                alt="Bug screenshot"
                                style={styles.thumbnail}
                              />
                            )}

                            <div style={styles.cardInfo}>
                              <div style={styles.reportTitle}>{report.title}</div>
                              <div style={styles.reportDescription}>
                                {isExpanded ? report.description : truncateText(report.description, 80)}
                              </div>
                              
                              {/* Frames */}
                              {report.frames && report.frames.length > 0 && (
                                <div style={styles.framesContainer}>
                                  {report.frames.slice(0, isExpanded ? undefined : 3).map((frame: string, idx: number) => (
                                    <span key={idx} style={styles.frameBadge}>
                                      {frame}
                                    </span>
                                  ))}
                                  {!isExpanded && report.frames.length > 3 && (
                                    <span style={styles.frameBadge}>
                                      +{report.frames.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}

                              <div style={styles.reportDate}>
                                {formatDate(report.createdAt)}
                              </div>
                            </div>
                          </div>

                          {/* Imagem expandida */}
                          {isExpanded && report.photoUrl && (
                            <div style={styles.expandedImageContainer}>
                              <img
                                src={report.photoUrl}
                                alt="Bug screenshot"
                                style={styles.expandedImage}
                              />
                            </div>
                          )}
                        </div>

                        {/* Dropdown de Status */}
                        <div style={styles.statusControl}>
                          <label style={styles.statusLabel}>Atualizar status:</label>
                          <select
                            value={report.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(report.userId, report.id, e.target.value);
                            }}
                            disabled={updatingStatus === report.id}
                            style={{
                              ...styles.statusSelect,
                              opacity: updatingStatus === report.id ? 0.6 : 1,
                              cursor: updatingStatus === report.id ? 'not-allowed' : 'pointer',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="reported">Reportado</option>
                            <option value="viewed">Visualizado</option>
                            <option value="development">Em Desenvolvimento</option>
                            <option value="done">Concluído</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #1976D2',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: theme.colors.text,
    margin: 0,
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  metricsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    backgroundColor: 'white',
    padding: '16px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '14px',
    color: '#666',
  },
  filtersContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    flex: '1 1 300px',
    minWidth: '200px',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    flex: 1,
    fontSize: '14px',
  },
  filterSelect: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    minWidth: '150px',
  },
  sortButton: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  reportsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  userGroup: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  userHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  userAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginRight: '12px',
  },
  userAvatarPlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#e9ecef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  userEmail: {
    fontSize: '13px',
    color: '#666',
  },
  userReportCount: {
    fontSize: '14px',
    fontWeight: '500',
    color: theme.colors.primary,
    backgroundColor: '#e3f2fd',
    padding: '4px 12px',
    borderRadius: '12px',
  },
  reportsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '16px',
    padding: '20px',
  },
  reportCard: {
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: 'white',
    transition: 'box-shadow 0.2s',
    cursor: 'pointer',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '8px',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  statusText: {
    fontSize: '13px',
    fontWeight: 'bold',
  },
  cardContent: {
    padding: '16px',
  },
  cardTop: {
    display: 'flex',
    gap: '12px',
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    flexShrink: 0,
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  reportTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: '8px',
  },
  reportDescription: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  framesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '8px',
  },
  frameBadge: {
    fontSize: '11px',
    padding: '4px 8px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    border: '1px solid #bbdefb',
  },
  reportDate: {
    fontSize: '12px',
    color: '#999',
  },
  expandedImageContainer: {
    marginTop: '16px',
    textAlign: 'center',
  },
  expandedImage: {
    maxWidth: '100%',
    maxHeight: '400px',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  statusControl: {
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statusLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#666',
  },
  statusSelect: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '13px',
    backgroundColor: 'white',
  },
};

// Adicionar keyframes de animação para o spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default BugReports;
